import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useRef,
} from "react";
import { getLoginData } from "../utility/AppStoreData";
import {
  ChatEvent,
  ChatMessage,
  MessageAcknowledgementEvent,
  ReadAllEvent,
  ReadEvent,
  StatusEvent,
  UpdateEvent,
} from "../utility/ChatSocket/ChatTypeDefinitions";
import {
  connectWebSocket,
  disconnectWebSocket,
  sendChatMessage,
  updateMessage,
  deleteMessage,
} from "../utility/ChatSocket/ChatWebSocketUtils";
import {
  CreateNewChat,
  fetchUserChatList,
  fetchUserStatus,
} from "../services/chatAPI";
import { handleApiError } from "../services/errorHandler";
import { toast } from "react-toastify";
import { NavigationProp, useNavigation } from "@react-navigation/native";

export interface ChatData {
  conversationId: string;
  members: string[];
  lastMessage: string;
  lastMessageTimestamp: number;
  lastMessageUser: string;
  createdAt: number;
  createdBy: string;
  type: "single" | "group" | "event" | "general";
  readStatus?: { [key: string]: boolean };
  groupIcon?: string;
  profilePicture?: string;
  defaultProfile?: string;
  lastMessageRead?: boolean;
  lastMessageId?: any;
  unreadCount?: number;
}

interface ChatContextProps {
  messages: ChatMessage[];
  chats: ChatData[];
  currentChat: ChatData | null;
  openChat: (chatId: string) => void;
  setSelectedName: (name: string) => void;
  chatData: ChatMessage[];
  setChatData: (messages: ChatMessage[]) => void;
  setChats: React.Dispatch<React.SetStateAction<ChatData[]>>;
  setUserData: React.Dispatch<
    React.SetStateAction<{ accessToken: string; username: string } | null>
  >;
  selectedName: string;
  userData: { accessToken: string; username: string } | null;
  isChatVisible: boolean;
  setChatVisible: (visible: boolean) => void;
  setCurrentChat: (chat: ChatData) => void;
  fetchUserChats: (conversationId: any, messagePopup?: any) => Promise<void>;
  createChat: (
    accessToken: string,
    userIds: string[],
    type: string
  ) => Promise<void>;
  sendMessage: (
    chatId: string,
    userId: string,
    messageContent: string,
    accessToken: string
  ) => void;
  handleUpdateMessage: (
    chatId: string,
    messageId: string,
    updateType: "react" | "read",
    updateData: any,
    accessToken: string,
    messageContent: string
  ) => void;
  deleteMessageForUser: (chatId: string, messageId: string) => void;
  deleteMessageForEveryone: (chatId: string, messageId: string) => void;
  loading: boolean;
  userStatuses: Record<string, Record<string, string>>;
  updateUserStatus: (
    conversationId: string,
    memberId: string,
    status: string
  ) => void;
  userDataRef: any;
  currentChatRef: any;
  setGeneralChatID: any;
  generalChatID: string;
  setChatNotificationCount: any;
  chatNotificationCount: any;
  directMessageCount: number;
  setDirectMessageCount: any;
  checkInStatuses: any;
}

interface ChatProviderProps {
  children: ReactNode;
  navigation?: NavigationProp<any>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const ChatProvider: React.FC<ChatProviderProps> = ({
  children,
  navigation,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chats, setChats] = useState<ChatData[]>([]);
  const [currentChat, setCurrentChat] = useState<ChatData | null>(null);
  const [isChatVisible, setChatVisible] = useState(false);
  const [chatData, setChatData] = useState<ChatMessage[]>([]);
  const currentChatRef = useRef<ChatData | null>(null);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<{
    accessToken: string;
    username: string;
  } | null>(null);
  const [selectedName, setSelectedName] = useState<string>("");
  const [userStatuses, setUserStatuses] = useState<
    Record<string, Record<string, string>>
  >({});
  const userDataRef = useRef(null);
  const [generalChatID, setGeneralChatIDState] = useState("");
  const generalChatIDRef = useRef<string | null>(null);
  const [chatNotificationCount, setChatNotificationCount] = useState(0);
  const [directMessageCount, setDirectMessageCount] = useState(0);
  const [checkInStatuses, setCheckInStatuses] = useState({});
  const chatsRef = useRef<any[]>([]);
  const nav = useNavigation();
  const setGeneralChatID = (chatID: string) => {
    setGeneralChatIDState(chatID);
    generalChatIDRef.current = chatID;
  };

  const updateUserStatus = (
    conversationId: string,
    memberId: string,
    status: string
  ) => {
    setUserStatuses((prevStatuses) => ({
      ...prevStatuses,
      [conversationId]: {
        ...prevStatuses[conversationId],
        [memberId]: status,
      },
    }));
  };

  const openChat = (chatId: string) => {
    const chat = chatsRef.current.find(
      (chat) => chat.conversationId === chatId
    );

    if (chat) {
      setChatVisible(true);
      setCurrentChat(chat);
      setSelectedName(
        chat.members.filter((m) => m !== userData?.username).join(", ")
      );

      handleUpdateMessage(chatId, "", "readAll", {}, userData.accessToken, "");
    }
  };

  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    const fetchLoginData = async () => {
      const loginData = await getLoginData();
      if (loginData && loginData.AccessToken) {
        setUserData({
          accessToken: loginData.AccessToken,
          username: loginData.username,
        });
      }
    };

    fetchLoginData();
  }, []);
  useEffect(() => {
    const unsubscribe = (navigation || nav).addListener("state", (e) => {
      const currentRoute = e.data.state.routes[e.data.state.index].name;
      if (currentRoute !== "Chat") {
        // Adjust 'Chat' to match your screen name in navigation
        currentChatRef.current = null;
        setCurrentChat(null);
        // Don't clear counts here - they should persist until messages are actually read
      }
    });

    return unsubscribe;
  }, [navigation, nav]);
  const fetchUserStatuses = async () => {
    if (!userDataRef.current) return;

    try {
      const statusResponse = await fetchUserStatus(
        userDataRef.current.accessToken
      );

      if (
        statusResponse.statusCode === 200 &&
        statusResponse.body.conversations
      ) {
        const initialStatuses: Record<string, Record<string, string>> = {};

        statusResponse.body.conversations.forEach((conversation: any) => {
          initialStatuses[conversation.conversationId] = conversation.members;
        });

        setUserStatuses(initialStatuses);
      }
    } catch (error) {
      console.error("Error fetching user statuses:", error);
    }
  };

  const handleMessage = (event: ChatEvent) => {
    if (event.type === "messageAcknowledgement") {
      const { timestamp, realMessageId } =
        event.data as MessageAcknowledgementEvent["data"];

      setChatData((prevChatData) =>
        prevChatData.map((msg) =>
          msg.messageId === timestamp
            ? { ...msg, messageId: realMessageId }
            : msg
        )
      );
    } else if (event.type === "AddMembers") {
      const addMembersData = event.data as any;

      setChats((prevChats: any) => {
        const updatedChats = prevChats.map((chat) =>
          chat.conversationId === addMembersData.conversationId
            ? { ...chat, ...addMembersData }
            : chat
        );

        return updatedChats;
      });

      if (
        addMembersData.conversationId === currentChatRef.current?.conversationId
      ) {
        setCurrentChat((prevCurrentChat) => {
          if (!prevCurrentChat) return prevCurrentChat;

          return {
            ...prevCurrentChat,
            members: addMembersData.members,
          };
        });
      }
      setChatNotificationCount((prevCount) => prevCount + 1);
    } else if (event.type === "NewChat") {
      const newChatData = event.data as ChatData;
      setChats((prevChats) => {
        const existingChat = prevChats.find(
          (chat) => chat.conversationId === newChatData.conversationId
        );

        if (existingChat) {
          return prevChats;
        }

        const updatedChats = [...prevChats, newChatData].sort(
          (a, b) =>
            (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0)
        );
        chatsRef.current = updatedChats;

        return updatedChats;
      });
      fetchUserStatuses();
      setChatNotificationCount((prevCount) => prevCount + 1);

      // Also increment directMessageCount if this is a "single" type chat
      if (newChatData.type === "single") {
        setDirectMessageCount((prevCount) => prevCount + 1);
      }
    } else if (event.type === "message") {
      const messageData = event.data as ChatMessage;
      console.log("Message data in chat context: ", messageData);

      if (
        messageData.conversationId === currentChatRef.current?.conversationId
      ) {
        setChatData((prevChatData) => [messageData, ...prevChatData]);

        handleUpdateMessage(
          messageData.conversationId,
          messageData.messageId,
          "read",
          { readStatus: { [messageData.userId]: true } },
          userDataRef.current.accessToken,
          messageData.messageContent
        );
      }

      if (messageData.conversationId === generalChatIDRef.current) {
        setChatData((prevChatData) => [messageData, ...prevChatData]);
      }
      const isUnreadByCurrentUser =
        messageData.readStatus?.[userDataRef.current?.username] === false &&
        messageData.conversationId !== currentChatRef.current?.conversationId;
      setChats((prevChats) => {
        const updatedChats = prevChats.map((chat) =>
          chat.conversationId === messageData.conversationId
            ? {
              ...chat,
              lastMessageUser: messageData.userId,
              lastMessage: messageData.messageContent,
              lastMessageTimestamp: messageData.timestamp,
              lastMessageRead: messageData.lastMessageRead || false,
              readStatus: messageData.readStatus || null,
              unreadCount:
                messageData.conversationId ===
                  currentChatRef.current?.conversationId
                  ? 0
                  : isUnreadByCurrentUser
                    ? (chat.unreadCount || 0) + 1
                    : chat.unreadCount || 0,
            }
            : chat
        );

        return updatedChats.sort(
          (a, b) =>
            (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0)
        );
      });
      if (
        messageData.conversationId !== currentChatRef.current?.conversationId
      ) {
        setChatNotificationCount((prevCount) => prevCount + 1);

        // Also increment directMessageCount if this is a "single" type chat
        const chat = chatsRef.current.find(
          (c) => c.conversationId === messageData.conversationId
        );
        if (chat && chat.type === "single") {
          setDirectMessageCount((prevCount) => prevCount + 1);
        }
      }
    } else if (event.type === "update") {
      const updateData = event.data as UpdateEvent;

      if (currentChatRef.current) {
        setChatData((prevChatData) =>
          prevChatData.map((msg) =>
            msg.messageId === updateData.messageId
              ? { ...msg, ...updateData.updateData }
              : msg
          )
        );
      }
      setChatNotificationCount((prevCount) => prevCount + 1);
    } else if (event.type === "read") {
      const readData = event.data as ReadEvent["data"];
      const { chatId, messageId, userId, messageContent } = readData;

      setChatData((prevChatData) =>
        prevChatData.map((msg) =>
          msg.conversationId === chatId && msg.messageContent === messageContent
            ? {
              ...msg,
              readStatus: {
                ...msg.readStatus,
                [userId]: true,
              },
            }
            : msg
        )
      );

      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.conversationId === chatId && chat.lastMessage === messageContent
            ? { ...chat, lastMessageRead: true }
            : chat
        )
      );
    } else if (event.type === "status") {
      const statusData = event.data as StatusEvent;
      setCheckInStatuses(statusData);
      const { userID, status } = statusData;

      setUserStatuses((prevStatuses) => {
        const updatedStatuses = { ...prevStatuses };

        Object.keys(updatedStatuses).forEach((conversationId) => {
          if (updatedStatuses[conversationId]?.[userID]) {
            updatedStatuses[conversationId][userID] = status;
          }
        });

        if (updatedStatuses[userID]) {
          updatedStatuses[userID].status = status;
        }

        return updatedStatuses;
      });
    } else if (event.type === "readAll") {
      const readAllData = event as ReadAllEvent;
      const { chatId, userId } = readAllData.data;

      setChatData((prevChatData) =>
        prevChatData.map((msg) =>
          msg.conversationId === chatId
            ? {
              ...msg,
              readStatus: {
                ...msg.readStatus,
                [userId]: true,
              },
            }
            : msg
        )
      );

      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.conversationId === chatId
            ? { ...chat, lastMessageRead: true, unreadCount: 0 }
            : chat
        )
      );
    } else if (event.type === "deleteForEveryone") {
      if ("chatId" in event.data && "messageId" in event.data) {
        const deleteData = event.data as { chatId: string; messageId: string };
        const { chatId, messageId } = deleteData;

        setChatData((prevChatData) =>
          prevChatData.filter(
            (msg) =>
              msg.conversationId !== chatId || msg.messageId !== messageId
          )
        );

        setChats((prevChats) =>
          prevChats.map((chat: any) =>
            chat.conversationId === chatId && chat.lastMessageId === messageId
              ? {
                ...chat,
                lastMessage: "This message was deleted",
                lastMessageTimestamp: null,
              }
              : chat
          )
        );
      } else {
        console.error(
          "deleteForEveryone event data is missing chatId or messageId."
        );
      }
    } else if (event.type === "deleteChat") {
      const deleteChatData = event.data as { conversationId: string };
      const { conversationId } = deleteChatData;

      setChats((prevChats) =>
        prevChats.filter((chat) => chat.conversationId !== conversationId)
      );
      if (currentChatRef.current?.conversationId === conversationId) {
        setCurrentChat(null);
      }

      setChatNotificationCount((prevCount) => prevCount + 1);
    }
  };

  const autoConnect = async () => {
    const loginData = await getLoginData();
    if (loginData && loginData.AccessToken) {
      setUserData({
        accessToken: loginData.AccessToken,
        username: loginData.username,
      });
      connectWebSocket(loginData.AccessToken, handleMessage);
      await fetchUserChats(null);
    }
  };

  const fetchUserChats = async (
    conversationId: any,
    messagePopup: any = false
  ) => {
    if (!userData || !userData.accessToken) {
      console.warn("User data or access token is missing");
      return;
    }
    setLoading(true);
    setCurrentChat(null);

    try {
      const chatListResponse = await fetchUserChatList(
        userData.accessToken,
        conversationId,
        messagePopup
      );

      if (chatListResponse.statusCode === 200 && chatListResponse.body.chats) {
        const fetchedChats = chatListResponse.body.chats;
        setChats(fetchedChats);
        chatsRef.current = fetchedChats;

        // Calculate total unread count from all chats (for header)
        const totalUnreadCount = fetchedChats.reduce((total, chat) => {
          return total + (chat.unreadCount || 0);
        }, 0);
        setChatNotificationCount(totalUnreadCount);

        // Calculate direct message count (type "single" only, for sidebar)
        const directMsgCount = fetchedChats.reduce((total, chat) => {
          if (chat.type === "single") {
            return total + (chat.unreadCount || 0);
          }
          return total;
        }, 0);
        setDirectMessageCount(directMsgCount);

        const statusResponse = await fetchUserStatus(userData.accessToken);

        if (
          statusResponse.statusCode === 200 &&
          statusResponse.body.conversations
        ) {
          const initialStatuses: Record<string, Record<string, string>> = {};

          statusResponse.body.conversations.forEach((conversation: any) => {
            initialStatuses[conversation.conversationId] = conversation.members;
          });

          setUserStatuses(initialStatuses);
        } else {
          console.error("Failed to fetch user statuses");
        }
      } else {
        console.error("Failed to fetch user chats");
      }
    } catch (error) {
      console.error("Error fetching user chats or statuses:", error);
    } finally {
      setLoading(false);
    }
  };

  const createChat = async (
    accessToken: string,
    userIds: string[],
    type: string
  ) => {
    if (!userData) return;
    setLoading(true);
    try {
      const response = await CreateNewChat(userIds, userData.accessToken, type);
      const chatId = response?.body?.chatId;

      console.log("Chats before openChat:", chats);

      if (response.statusCode === 200) {
        toast.success("Chat created successfully");
      } else if (response.statusCode === 400) {
        toast.info("Chat already exists");
      } else {
        handleApiError(
          response.status,
          response.body.message || "An error occurred"
        );
        return;
      }

      await fetchUserChats(null);

      if (chatId) {
        console.log("Opening chat with chatId:", chatId);
        openChat(chatId);
      }
    } catch (error) {
      console.error("Error creating chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = (
    chatId: string,
    userId: string,
    messageContent: string,
    accessToken: string
  ) => {
    const newMessage = {
      messageId: `${new Date().getTime()}`,
      conversationId: chatId,
      userId,
      messageContent,
      timestamp: Date.now(),
      readStatus: { [userId]: true },
      reactions: {},
      deletedFor: [],
      deletedForEveryone: false,
    };
    sendChatMessage(
      chatId,
      userId,
      messageContent,
      accessToken,
      newMessage.messageId
    );

    setChatData((prevChatData) => [newMessage, ...prevChatData]);
  };

  const handleUpdateMessage = (
    chatId: string,
    messageId: string,
    updateType: "react" | "read" | "readAll",
    updateData: any,
    accessToken: string,
    messageContent: string
  ) => {
    updateMessage(
      chatId,
      messageId,
      updateType,
      updateData,
      accessToken,
      messageContent
    );
  };

  const deleteMessageForUser = (chatId: string, messageId: string) => {
    const userId = userDataRef.current.username;
    const accessToken = userDataRef.current.accessToken;
    deleteMessage(chatId, messageId, "deleteForUser", { userId }, accessToken);
  };

  const deleteMessageForEveryone = (chatId: string, messageId: string) => {
    const accessToken = userDataRef.current.accessToken;
    deleteMessage(chatId, messageId, "deleteForEveryone", {}, accessToken);
  };

  useEffect(() => {
    autoConnect();
    return () => {
      disconnectWebSocket();
    };
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        chatNotificationCount,
        openChat,
        setChatNotificationCount,
        directMessageCount,
        setDirectMessageCount,
        loading,
        selectedName,
        setSelectedName,
        setGeneralChatID,
        generalChatID,
        setChatVisible,
        isChatVisible,
        chats,
        currentChat,
        setUserData,
        chatData,
        setChats,
        setChatData,
        userData,
        setCurrentChat,
        updateUserStatus,
        userStatuses,
        fetchUserChats,
        createChat,
        checkInStatuses,
        sendMessage,
        handleUpdateMessage,
        deleteMessageForUser,
        userDataRef,
        currentChatRef,
        deleteMessageForEveryone,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    // Return safe defaults when used outside provider (e.g., for non-logged-in users)
    console.warn("useChat called outside ChatProvider - returning defaults");
    return {
      messages: [],
      chats: [],
      currentChat: null,
      openChat: () => { },
      setSelectedName: () => { },
      chatData: [],
      setChatData: () => { },
      setChats: () => { },
      setUserData: () => { },
      selectedName: "",
      userData: null,
      isChatVisible: false,
      setChatVisible: () => { },
      setCurrentChat: () => { },
      chatNotificationCount: 0,
      setChatNotificationCount: () => { },
      directMessageCount: 0,
      setDirectMessageCount: () => { },
      fetchUserChats: async () => { },
      createChat: async () => { },
      sendMessage: () => { },
      handleUpdateMessage: () => { },
      deleteMessageForUser: () => { },
      deleteMessageForEveryone: () => { },
      loading: false,
      userStatuses: {},
      updateUserStatus: () => { },
      userDataRef: { current: null },
      currentChatRef: { current: null },
      checkInStatuses: {},
      setGeneralChatID: () => { },
      generalChatID: "",
    };
  }
  return context;
};
